import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Repeat, ArrowRight } from "lucide-react";
import { Logo } from "../components/Logo";
import Footer from "../components/Footer";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Only show the "session expired" banner on first paint. Once the user
  // interacts (typing/submitting) or a real login error surfaces, hide it —
  // it should never live alongside a fresh error state (Prompt 06b).
  const [sessionExpired, setSessionExpired] = useState(() =>
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("session") === "expired"
  );

  if (user && user.email) {
    // Honour ?next= (used by /qbo/launch and /qbo/connect) with a
    // safe same-origin check so the parameter can't be used as an
    // open-redirect vector.
    const next = typeof window !== "undefined"
      ? (new URLSearchParams(window.location.search).get("next") || "")
      : "";
    const target = next.startsWith("/") && !next.startsWith("//") ? next : "/";
    return <Navigate to={target} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSessionExpired(false);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      if (typeof window !== "undefined" && window.location.search.includes("session=")) {
        window.history.replaceState({}, "", "/login");
      }
      const next = typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("next") || "")
        : "";
      const target = next.startsWith("/") && !next.startsWith("//") ? next : "/";
      navigate(target);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
      {/* Visual panel */}
      <div className="hidden lg:flex relative overflow-hidden border-r border-neutral-900">
        <img
          src="https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg"
          alt="server room"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-neutral-900/60 grid-overlay" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <Link to="/" className="block" data-testid="login-logo">
            <Logo dark className="h-8" testid="login-logo-img" />
          </Link>
          <div>
            <div className="label-mini text-neutral-300">middleware</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold leading-[1.05] mt-3">
              Stop re-typing your vendors' invoices.
              <br />
              <span className="text-neutral-400">— vendor invoices, delivered as bills.</span>
            </h2>
            <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
              <div>
                <div className="label-mini text-neutral-400">fields</div>
                <div className="font-display text-3xl font-bold">10</div>
              </div>
              <div>
                <div className="label-mini text-neutral-400">cadence</div>
                <div className="font-display text-3xl font-bold">5m</div>
              </div>
              <div>
                <div className="label-mini text-neutral-400">direction</div>
                <div className="font-display text-3xl font-bold">↔</div>
              </div>
            </div>
          </div>
          <div className="label-mini text-neutral-400">v1.0 · build 0001</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm" data-testid="login-form">
          <div className="label-mini">access</div>
          <h1 className="font-display text-4xl tracking-tight font-bold mt-2">Sign in</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Sign in with your registered company account.
          </p>

          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            {sessionExpired && (
              <div
                className="border border-amber-500 text-amber-700 text-xs px-3 py-2"
                data-testid="session-expired-banner"
              >
                Your session expired. Please sign in again.
              </div>
            )}
            <div>
              <label className="label-mini block mb-2" htmlFor="email">email</label>
              <input
                id="email"
                type="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-mini block mb-2" htmlFor="password">password</label>
              <input
                id="password"
                type="password"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="border border-red-600 text-red-600 text-xs px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "signing in…" : "sign in"} <ArrowRight size={14} />
            </button>
          </form>

          <div className="mt-6 text-xs text-neutral-500">
            no account?{" "}
            <Link to="/register" className="underline text-neutral-900" data-testid="login-register-link">
              create one
            </Link>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
